import React, { useMemo } from 'react';
import {
    ApartmentOutlined,
    BankOutlined,
    BuildOutlined,
    ControlOutlined,
    EnvironmentOutlined,
    FlagOutlined,
    LockOutlined,
    OrderedListOutlined,
    PartitionOutlined,
    SettingOutlined
} from '@ant-design/icons';
import { Card, Col, Divider, Row, Space, Typography } from 'antd';
import { Link } from 'react-router-dom';
import Layout from './Layout';
import {
    canAtLeast,
    getCachedPageAccess,
    pathToPageKey
} from '../utils/pageAccess';

const { Title, Text } = Typography;

const SECTIONS = [
    {
        title: 'Проекты и организации',
        items: [
            {
                path: '/task-manager/projects',
                title: 'Проекты',
                description: 'Стройка, обслуживание станций',
                icon: <BuildOutlined />
            },
            {
                path: '/task-manager/organizations',
                title: 'Организации заявок',
                description: 'Создатель, исполнитель, монтажники',
                icon: <BankOutlined />
            },
            {
                path: '/task-manager/sites',
                title: 'Участки',
                description: 'Участки и права ролей на них',
                icon: <EnvironmentOutlined />
            }
        ]
    },
    {
        title: 'Права и маршруты',
        items: [
            {
                path: '/task-manager/access',
                title: 'Конструктор прав',
                description: 'Права полей по роли, этапу и орг.',
                icon: <ControlOutlined />
            },
            {
                path: '/task-manager/stage-routes',
                title: 'Маршруты этапов',
                description: 'Переходы между этапами',
                icon: <ApartmentOutlined />
            },
            {
                path: '/task-manager/work-type-stages',
                title: 'Этапы по видам работ',
                description: 'АВР, ППР и другие',
                icon: <PartitionOutlined />
            },
            {
                path: '/task-manager/field-permissions',
                title: 'Права на поля (legacy)',
                description: 'Старая матрица прав',
                icon: <LockOutlined />,
                adminOnly: true
            }
        ]
    },
    {
        title: 'Поля и справочники',
        items: [
            {
                path: '/task-manager/fields',
                title: 'Поля задач',
                description: 'Основные и кастомные поля',
                icon: <SettingOutlined />
            },
            {
                path: '/task-manager/dictionaries',
                title: 'Справочники',
                description: 'Виды работ, БС и др.',
                icon: <OrderedListOutlined />
            },
            {
                path: '/task-manager/statuses',
                title: 'Статусы задач',
                description: 'Справочник этапов/статусов',
                icon: <FlagOutlined />
            }
        ]
    }
];

const TaskManagerSettingsPage = () => {
    const isAdmin = (() => {
        try {
            const user = JSON.parse(
                localStorage.getItem('user') || 'null'
            );
            const rawRole =
                user?.role_id ??
                user?.roleId ??
                localStorage.getItem('roleId');
            return Number(rawRole) === 1;
        } catch (_) {
            return Number(localStorage.getItem('roleId')) === 1;
        }
    })();

    const visibleSections = useMemo(() => {
        const access = getCachedPageAccess() || {};

        const canSee = item => {
            if (item.adminOnly && !isAdmin) {
                return false;
            }
            const key = pathToPageKey(item.path);
            if (!key) {
                return true;
            }
            if (isAdmin) {
                return true;
            }
            if (!access[key]) {
                return true;
            }
            return canAtLeast(access[key], 'view');
        };

        return SECTIONS.map(section => ({
            ...section,
            items: section.items.filter(canSee)
        })).filter(section => section.items.length > 0);
    }, [isAdmin]);

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    direction="vertical"
                    size={4}
                    style={{ marginBottom: 24 }}
                >
                    <Title level={3} style={{ margin: 0 }}>
                        Настройки менеджера задач
                    </Title>
                    <Text type="secondary">
                        Проекты, организации, права, маршруты и
                        справочники
                    </Text>
                </Space>

                {visibleSections.map(section => (
                    <div key={section.title} style={{ marginBottom: 28 }}>
                        <Divider orientation="left">
                            {section.title}
                        </Divider>
                        <Row gutter={[16, 16]}>
                            {section.items.map(item => (
                                <Col
                                    xs={24}
                                    sm={12}
                                    md={8}
                                    lg={6}
                                    key={item.path}
                                >
                                    <Link to={item.path}>
                                        <Card
                                            hoverable
                                            style={{
                                                height: '100%',
                                                borderRadius: 8
                                            }}
                                        >
                                            <Space
                                                direction="vertical"
                                                size={8}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: 24,
                                                        color: '#1890ff'
                                                    }}
                                                >
                                                    {item.icon}
                                                </div>
                                                <Text strong>
                                                    {item.title}
                                                </Text>
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        fontSize: 12
                                                    }}
                                                >
                                                    {item.description}
                                                </Text>
                                            </Space>
                                        </Card>
                                    </Link>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}
            </div>
        </Layout>
    );
};

export default TaskManagerSettingsPage;
